/**
 * Small presentational primitives every finance screen shares: a page header,
 * a labelled field wrapper, a select bound to a `{value,label}` list, an ISO
 * date field, and a decimal field that keeps the typed text EXACTLY as typed
 * and reports the canonical wire string separately.
 *
 * The decimal field is the important one. Finance amounts are decimal strings
 * with fixed scales; the field never rounds, never reformats mid-typing, and
 * never converts to a JS number — it just tells the caller whether the current
 * text canonicalizes and, when it does, what the wire value is.
 */
import type { ReactNode } from "react";
import {
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@erp/ui";
import type { BadgeProps } from "@erp/ui";

import { normalizeDecimal, type DecimalOptions } from "../decimal";

export interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
}

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description === undefined ? null : (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions === undefined ? null : (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

export interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
  className?: string;
  children: ReactNode;
}

export function Field({ id, label, hint, error, className, children }: FieldProps) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint === undefined ? null : <p className="text-xs text-muted-foreground">{hint}</p>}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  hint?: string;
  error?: string | null;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function SelectField({
  id,
  label,
  value,
  onValueChange,
  options,
  placeholder,
  hint,
  error,
  disabled,
  className,
  triggerClassName,
}: SelectFieldProps) {
  return (
    <Field id={id} label={label} hint={hint} error={error} className={className}>
      {/* key: remount when the controlled value is cleared, so Radix drops its label. */}
      <Select key={value} value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger id={id} className={triggerClassName} aria-invalid={Boolean(error)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  error?: string | null;
  disabled?: boolean;
  className?: string;
  /** Codes, IBANs and references are LTR even under an RTL locale. */
  ltr?: boolean;
  maxLength?: number;
}

export function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  error,
  disabled,
  className,
  ltr,
  maxLength,
}: TextFieldProps) {
  return (
    <Field id={id} label={label} hint={hint} error={error} className={className}>
      <Input
        id={id}
        dir={ltr ? "ltr" : undefined}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={Boolean(error)}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

export interface DateFieldProps {
  id: string;
  label: string;
  /** ISO `YYYY-MM-DD`, exactly as the contract sends and accepts it. */
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}

export function DateField({
  id,
  label,
  value,
  onChange,
  hint,
  error,
  disabled,
  className,
}: DateFieldProps) {
  return (
    <Field id={id} label={label} hint={hint} error={error} className={className}>
      <Input
        id={id}
        type="date"
        // physical-ok: a date control's internal segment order is owned by the platform
        dir="ltr"
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

export interface DecimalFieldProps {
  id: string;
  label: string;
  /** Raw text, kept verbatim so a half-typed "12." is never destroyed. */
  value: string;
  onChange: (raw: string) => void;
  decimal: DecimalOptions;
  hint?: string;
  /** Shown when the text is present and does not canonicalize. */
  invalidMessage?: string;
  error?: string | null;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export function DecimalField({
  id,
  label,
  value,
  onChange,
  decimal,
  hint,
  invalidMessage,
  error,
  disabled,
  className,
  placeholder,
}: DecimalFieldProps) {
  // A form is often seeded straight from a wire record, and an OPTIONAL member
  // of that record is simply absent. Coalescing here keeps the field
  // controlled — and keeps the whole screen alive — instead of throwing on a
  // legitimately missing value.
  const text = value ?? "";
  const malformed = text.trim() !== "" && normalizeDecimal(text, decimal) === null;
  const message = error ?? (malformed ? (invalidMessage ?? null) : null);
  return (
    <Field id={id} label={label} hint={hint} error={message} className={className}>
      <Input
        id={id}
        // physical-ok: numeric entry is forced LTR even in RTL locales
        dir="ltr"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={Boolean(message)}
        onChange={(event) => onChange(event.target.value)}
        className="text-end tabular-nums"
      />
    </Field>
  );
}

export interface DefinitionRow {
  label: string;
  value: ReactNode;
}

/** Compact key/value summary used by every document header. */
export function DefinitionList({ rows }: { rows: readonly DefinitionRow[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((row) => (
        <div key={row.label} className="flex min-w-0 flex-col">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">{row.label}</dt>
          <dd className="truncate text-sm font-medium">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export type StatusVariant = NonNullable<BadgeProps["variant"]>;

/** Status pill with an explicit code→variant map; unknown codes stay neutral. */
export function StatusBadge({
  status,
  label,
  variants,
}: {
  status: string;
  label: string;
  variants: Record<string, StatusVariant>;
}) {
  return <Badge variant={variants[status] ?? "outline"}>{label}</Badge>;
}

/** A destructive banner for a refusal the screen must not hide behind a toast. */
export function ProblemBanner({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
    >
      {children}
    </p>
  );
}

/** A neutral banner for information the operator must read before acting. */
export function NoticeBanner({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">{children}</p>
  );
}
